import { AfterViewInit, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize, map, of } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { MessageModule } from '@openng/optimus-ui/message';
import { TagModule } from '@openng/optimus-ui/tag';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { FormsModule } from '@angular/forms';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { DealDocumentsService, IVoidContractReason } from '@zayan-erp/sales';
import { CurrencyFormatPipe, DropdownData, GenericDropdownComponent, rxResourceFixed } from '@zayan-erp/shared';
import {
  DSM_ENVELOPE_STATUS_LABELS,
  DSM_ENVELOPE_STATUS_SEVERITIES,
  DSM_SIGNING_TYPE_LABELS,
  DsmAddendumEnvelope,
  DsmEnvelopeStatus,
  DsmSignOffQueryParam,
  DsmSignOffSubTab,
  DsmSignOffTagSeverity
} from '../../../models/dsm-addendum-sign-off.models';
import { DsmDealOption } from '../../../models/dsm-deal-options-view.models';
import { DsmAddendumSignOffService } from '../../../services/dsm-addendum-sign-off.service';
import { DsmApsSignOffContextService } from '../../../services/dsm-aps-sign-off-context.service';
import { DsmApsDealContextService } from '../../../services/dsm-aps-deal-context.service';
import { DsmApsService } from '../../../services/dsm-aps.service';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';
import { DsmSignOffAddendumListComponent } from '../dsm-sign-off-addendum-list/dsm-sign-off-addendum-list.component';
import { DsmApsSignOffSignersListComponent } from '../dsm-aps-sign-off-signers-list/dsm-aps-sign-off-signers-list.component';
import { DsmApsSignOffAddendumsComponent } from '../dsm-aps-sign-off-addendums/dsm-aps-sign-off-addendums.component';
import { dsmEnvelopeLabel } from '../../../utils/dsm-addendum-envelope.util';

type EnvelopeCard = {
  envelope: DsmAddendumEnvelope;
  label: string;
  flowTag: string | null;
  statusTag: { label: string; severity: DsmSignOffTagSeverity } | null;
  hasUpdatedAddendums: boolean;
  addendums: DsmDealOption[];
  activeTab: DsmSignOffSubTab;
  showSignersTab: boolean;
  isDownloading: boolean;
};

@Component({
  selector: 'dsm-aps-sign-off-tracking',
  standalone: true,
  host: { class: 'flex flex-col flex-1 overflow-hidden' },
  imports: [
    ButtonModule,
    DialogModule,
    MessageModule,
    TagModule,
    SelectButtonModule,
    FormsModule,
    TextareaModule,
    CurrencyFormatPipe,
    GenericDropdownComponent,
    DsmSignOffAddendumListComponent,
    DsmApsSignOffSignersListComponent,
    DsmApsSignOffAddendumsComponent
  ],
  templateUrl: './dsm-aps-sign-off-tracking.component.html'
})
export class DsmApsSignOffTrackingComponent implements AfterViewInit {
  readonly dealId = input.required<string>();

  readonly subTabOptions: { label: string; value: DsmSignOffSubTab }[] = [
    { label: 'Signers List', value: DsmSignOffSubTab.Signers },
    { label: 'Addendums', value: DsmSignOffSubTab.Addendums }
  ];

  readonly #signOffService = inject(DsmAddendumSignOffService);
  readonly #signOffContext = inject(DsmApsSignOffContextService);
  readonly #dealContext = inject(DsmApsDealContextService);
  readonly #dealOptionsService = inject(DsmDealOptionsService);
  readonly #dsmApsService = inject(DsmApsService);
  readonly #dealDocService = inject(DealDocumentsService);
  readonly #toastrService = inject(ToastrService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);

  readonly selectedEnvelopeId = toSignal(
    this.#route.queryParamMap.pipe(map(params => params.get(DsmSignOffQueryParam.EnvelopeId))),
    {
      initialValue: this.#route.snapshot.queryParamMap.get(DsmSignOffQueryParam.EnvelopeId)
    }
  );

  readonly envelopesResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId() }),
    defaultValue: [] as DsmAddendumEnvelope[],
    stream: ({ params }) => {
      if (!params.dealId) return of([] as DsmAddendumEnvelope[]);
      return this.#signOffService.getEnvelopes(params.dealId);
    }
  });

  readonly dealHeaderResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.dealId) return of(null);
      return this.#dsmApsService.getDealHeader(params.dealId);
    }
  });

  readonly #lotId = computed(
    () => this.#dealContext.effectiveLotId() || (this.dealHeaderResource.value()?.lot?.id ?? '')
  );

  readonly dealAddendumsResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId(), lotId: this.#lotId() }),
    defaultValue: [] as DsmDealOption[],
    stream: ({ params }) => {
      if (!params.dealId || !params.lotId) return of([] as DsmDealOption[]);
      return this.#dealOptionsService.getAddendums(params.lotId, params.dealId, false);
    }
  });

  /** Newest envelope first; the API returns them oldest-first. */
  readonly envelopes = computed(() => [...(this.envelopesResource.value() ?? [])].reverse());
  readonly isLoading = computed(() => this.envelopesResource.isLoading());

  readonly #addendumsById = computed(
    () => new Map((this.dealAddendumsResource.value() ?? []).map(addendum => [addendum.id, addendum]))
  );

  readonly #tabs = signal<Record<string, DsmSignOffSubTab>>({});

  readonly #downloadingIds = signal<ReadonlySet<string>>(new Set());

  /** The envelope opened from "View Envelope" is pinned to the top; the rest keep their order. */
  readonly cards = computed<EnvelopeCard[]>(() => {
    const pinnedId = this.selectedEnvelopeId();
    const envelopes = [...this.envelopes()];
    if (pinnedId) {
      const index = envelopes.findIndex(envelope => envelope.dealDocumentId === pinnedId);
      if (index > 0) envelopes.unshift(...envelopes.splice(index, 1));
    }

    const tabs = this.#tabs();
    const downloadingIds = this.#downloadingIds();

    return envelopes.map(envelope => {
      const addendums = this.#resolveAddendums(envelope);
      const activeTab = tabs[envelope.dealDocumentId] ?? DsmSignOffSubTab.Signers;
      return {
        envelope,
        label: dsmEnvelopeLabel(envelope),
        flowTag: DSM_SIGNING_TYPE_LABELS[envelope.signingType] ?? null,
        statusTag: this.#statusTag(envelope),
        hasUpdatedAddendums: addendums.some(addendum => addendum.signOff?.hasMismatch),
        addendums,
        activeTab,
        showSignersTab: activeTab === DsmSignOffSubTab.Signers,
        isDownloading: downloadingIds.has(envelope.dealDocumentId)
      };
    });
  });

  readonly pinnedEnvelopeLabel = computed(() => {
    const pinnedId = this.selectedEnvelopeId();
    if (!pinnedId) return null;
    const match = this.envelopes().find(envelope => envelope.dealDocumentId === pinnedId);
    return match ? dsmEnvelopeLabel(match) : null;
  });

  readonly voidTarget = signal<DsmAddendumEnvelope | null>(null);
  readonly isVoiding = signal(false);

  readonly voidReasons = signal<IVoidContractReason[]>([]);
  readonly selectedVoidReasonId = signal('');
  readonly voidComment = signal('');
  readonly showVoidReasonError = signal(false);

  readonly voidReasonsDropdownItems = computed<DropdownData[]>(() =>
    this.voidReasons().map(reason => ({ id: reason.id, firstLabel: reason.description }))
  );
  readonly selectedVoidReason = computed<IVoidContractReason | null>(
    () => this.voidReasons().find(reason => reason.id === this.selectedVoidReasonId()) ?? null
  );
  readonly voidReasonRequiresComment = computed(() => this.selectedVoidReason()?.requiresComment ?? false);

  readonly voidAddendums = computed<DsmDealOption[]>(() => {
    const envelope = this.voidTarget();
    return envelope ? this.#resolveAddendums(envelope) : [];
  });

  readonly voidPreviewResource = rxResourceFixed({
    params: () => ({ dealDocumentId: this.voidTarget()?.dealDocumentId ?? '' }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.dealDocumentId) return of(null);
      return this.#signOffService.getVoidPreview(params.dealDocumentId);
    }
  });

  readonly isVoidPreviewLoading = computed(() => this.voidPreviewResource.isLoading());
  readonly voidBlockReason = computed(() => this.voidPreviewResource.value()?.blockReason ?? null);
  readonly canConfirmVoid = computed(() => {
    const reason = this.selectedVoidReason();
    if (!reason || !(this.voidPreviewResource.value()?.canVoid ?? false)) return false;
    return reason.requiresComment ? this.voidComment().trim().length > 0 : true;
  });

  constructor() {
    // Publish the header so the shared addendum rows get lot/model/elevation on a direct landing.
    effect(() => {
      const deal = this.dealHeaderResource.value();
      if (deal && !this.#dealContext.currentDeal()) {
        this.#dealContext.currentDeal.set(deal);
      }
    });

    effect(() => {
      if (this.#signOffContext.reload()) {
        this.envelopesResource.reload();
        this.dealAddendumsResource.reload();
        this.#signOffContext.reload.set(false);
      }
    });

    this.#dealDocService.getContractVoidReason().subscribe({
      next: reasons => this.voidReasons.set(reasons ?? [])
    });

    this.#destroyRef.onDestroy(() => this.#signOffContext.reset());
  }

  ngAfterViewInit(): void {
    if (this.getDocuSignReturnEvent() === 'signing_complete') {
      this.#toastrService.success(
        'Document has been signed. The status might take some time to reflect document signature status on this page',
        'Success',
        {
          timeOut: 20000,
          closeButton: true
        }
      );

      setTimeout(() => {
        this.envelopesResource.reload();
        this.dealAddendumsResource.reload();
        this.removeEventQueryParamFromUrl();
      }, 20000);
    }
  }

  removeEventQueryParamFromUrl(): void {
    if (this.#route.snapshot.queryParamMap.has('event')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('event');
      window.history.replaceState({}, '', url.toString());
    }
  }

  private getDocuSignReturnEvent(): string | null {
    const events = new URLSearchParams(window.location.search).getAll('event');
    if (events.length === 0) {
      return null;
    }
    return events[events.length - 1] ?? null;
  }

  #resolveAddendums(envelope: DsmAddendumEnvelope): DsmDealOption[] {
    const byId = this.#addendumsById();
    return envelope.addendums
      .map(summary => byId.get(summary.addendumId))
      .filter((addendum): addendum is DsmDealOption => !!addendum);
  }

  #statusTag(envelope: DsmAddendumEnvelope): { label: string; severity: DsmSignOffTagSeverity } | null {
    if (envelope.status === DsmEnvelopeStatus.Pending || envelope.status === DsmEnvelopeStatus.Sent) return null;
    return {
      label: DSM_ENVELOPE_STATUS_LABELS[envelope.status] ?? envelope.status,
      severity: DSM_ENVELOPE_STATUS_SEVERITIES[envelope.status] ?? 'info'
    };
  }

  selectTab(dealDocumentId: string, tab: DsmSignOffSubTab): void {
    this.#tabs.update(tabs => ({ ...tabs, [dealDocumentId]: tab }));
  }

  clearEnvelopeFilter(): void {
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { [DsmSignOffQueryParam.EnvelopeId]: null },
      queryParamsHandling: 'merge'
    });
  }

  openVoidDialog(envelope: DsmAddendumEnvelope): void {
    this.selectedVoidReasonId.set('');
    this.voidComment.set('');
    this.showVoidReasonError.set(false);
    this.voidTarget.set(envelope);
  }

  closeVoidDialog(): void {
    this.voidTarget.set(null);
  }

  confirmVoidEnvelope(): void {
    const envelope = this.voidTarget();
    const reason = this.selectedVoidReason();
    if (!envelope || !reason || !this.canConfirmVoid()) {
      this.showVoidReasonError.set(true);
      return;
    }

    const reasonText = reason.requiresComment ? this.voidComment().trim() : reason.description;

    this.isVoiding.set(true);
    this.#signOffService
      .void(envelope.dealDocumentId, reasonText)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.isVoiding.set(false);
          this.voidTarget.set(null);
          this.envelopesResource.reload();
          this.dealAddendumsResource.reload();
        },
        error: () => this.isVoiding.set(false)
      });
  }

  downloadPdf(envelope: DsmAddendumEnvelope): void {
    const { dealDocumentId } = envelope;
    if (this.#downloadingIds().has(dealDocumentId)) return;

    const lotNo = this.#dealContext.currentDeal()?.lot?.lotNo;
    const fileName = `${lotNo ? `${lotNo}-` : ''}${dsmEnvelopeLabel(envelope)}.pdf`;

    this.#downloadingIds.update(ids => new Set(ids).add(dealDocumentId));
    this.#signOffService
      .downloadSigned(dealDocumentId, fileName)
      .pipe(
        finalize(() =>
          this.#downloadingIds.update(ids => {
            const next = new Set(ids);
            next.delete(dealDocumentId);
            return next;
          })
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }
}
